import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, TextField, Button, Typography, Alert, InputAdornment,
  IconButton, CircularProgress, Divider,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Person, CheckCircle, Cancel, CardGiftcard, ArrowBack } from '@mui/icons-material';

import { registerUser, selectAuthLoading, selectAuthError, clearError } from '../../redux/slices/authSlice';
import systemSettingsService from '../../services/systemSettingsService';
import { injectSnippet } from '../../utils/injectSnippet';
import AuthMethodChooser from '../../components/auth/AuthMethodChooser';
import PhoneNumberField from '../../components/common/PhoneNumberField';

const RegisterPage = () => {
  const dispatch = useDispatch();
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '',
    // Prefilled from a referral share link (?ref=CODE) — still editable, so
    // someone who typo'd/forgot their friend's code by hand can fix it.
    referralCode: (searchParams.get('ref') || '').toUpperCase(),
  });
  const [showPw, setShowPw] = useState(false);
  const [success, setSuccess] = useState(false);
  const [localError, setLocalError] = useState('');
  // "Continue with Email" / "Continue with Google" / "Continue with Apple"
  // are offered as three equal options up front — this just reveals the
  // existing registration form in place of the chooser; Google/Apple
  // complete sign-up right from the chooser itself.
  const [showEmailForm, setShowEmailForm] = useState(false);

  // The actual "a lead just converted" moment for this app — fired once,
  // right here, not site-wide (see AuthLayout.jsx for the GTM head/body
  // snippets, which DO run across every public page; this one is scoped to
  // this single event instead).
  useEffect(() => {
    if (!success) return;
    let cancelled = false;
    let cleanup = () => { };
    systemSettingsService
      .getIntegrationSnippets()
      .then(({ data }) => {
        if (cancelled) return;
        cleanup = injectSnippet(data.data?.integrations?.leadConversionSnippet, document.body);
      })
      .catch(() => {
        /* Tracking is best-effort — never block/break the success screen over it. */
      });
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [success]);

  const handleChange = (e) => {
    dispatch(clearError());
    setLocalError('');
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const passwordsMatch = form.confirmPassword.length > 0 && form.password === form.confirmPassword;
  const passwordsMismatch = form.confirmPassword.length > 0 && form.password !== form.confirmPassword;
  const passwordLongEnough = form.password.length >= 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }
    const { confirmPassword, ...payload } = form;
    const result = await dispatch(registerUser(payload));
    if (registerUser.fulfilled.match(result)) {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Box sx={{ fontSize: 48, mb: 2 }}>📧</Box>
        <Typography variant="h4" fontWeight={700} mb={1.5}>Check your email</Typography>
        <Typography color="text.secondary" mb={3}>
          We sent a verification link to <strong>{form.email}</strong>. Click it to activate your account.
        </Typography>
        <Button variant="outlined" component={Link} to="/login" fullWidth>
          Back to Login
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h3" fontWeight={700} color="text.primary">
        Create account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Join Waypoint Imports to manage your shipments
      </Typography>

      {(error || localError) && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error || localError}
        </Alert>
      )}

      {!showEmailForm ? (
        <AuthMethodChooser onContinueWithEmail={() => setShowEmailForm(true)} />
      ) : (
        <>
          <Button
            type="button"
            onClick={() => setShowEmailForm(false)}
            startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontSize: 13,
              alignSelf: 'flex-start',
              mb: 1.5,
              px: 0,
              '&:hover': { backgroundColor: 'transparent', color: 'primary.main' },
            }}
          >
            Use a different method
          </Button>

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="First name"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            required
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start"><Person sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
              },
            }}
          />
          <TextField
            label="Last name"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            required
          />
        </Box>

        <TextField
          label="Email address"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          required
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
            },
          }}
        />

        <PhoneNumberField
          label="Phone number (optional)"
          value={form.phone}
          onChange={(phone) => {
            dispatch(clearError());
            setLocalError('');
            setForm((p) => ({ ...p, phone }));
          }}
        />

        <TextField
          label="Referral code (optional)"
          name="referralCode"
          value={form.referralCode}
          onChange={(e) => {
            dispatch(clearError());
            setLocalError('');
            setForm((p) => ({ ...p, referralCode: e.target.value.toUpperCase() }));
          }}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><CardGiftcard sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
            },
          }}
        />

        <TextField
          label="Password"
          name="password"
          type={showPw ? 'text' : 'password'}
          value={form.password}
          onChange={handleChange}
          required
          error={form.password.length > 0 && !passwordLongEnough}
          helperText={
            form.password.length > 0 && !passwordLongEnough
              ? "Must be at least 8 characters"
              : "Min. 8 characters"
          }
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><Lock sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPw((p) => !p)}>
                    {showPw ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <TextField
          label="Confirm password"
          name="confirmPassword"
          type={showPw ? 'text' : 'password'}
          value={form.confirmPassword}
          onChange={handleChange}
          required
          error={passwordsMismatch}
          helperText={
            passwordsMismatch
              ? "Passwords do not match"
              : passwordsMatch
                ? "Passwords match"
                : ""
          }
          slotProps={{
            formHelperText: {
              sx: { color: passwordsMatch ? "success.main" : undefined },
            },
            input: {
              startAdornment: <InputAdornment position="start"><Lock sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
              endAdornment: form.confirmPassword.length > 0 && (
                <InputAdornment position="end">
                  {passwordsMatch ? (
                    <CheckCircle sx={{ fontSize: 18, color: "success.main" }} />
                  ) : (
                    <Cancel sx={{ fontSize: 18, color: "error.main" }} />
                  )}
                </InputAdornment>
              ),
            },
          }}
        />

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={loading}
          sx={{ mt: 0.5, py: 1.375, fontSize: 14, fontWeight: 600 }}
        >
          {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Create Account'}
        </Button>
          </Box>
        </>
      )}

      <Divider sx={{ my: 3 }}>
        <Typography variant="caption" color="text.disabled">
          OR
        </Typography>
      </Divider>

      <Button
        component={Link}
        to="/login"
        variant="outlined"
        size="large"
        fullWidth
        sx={{ py: 1.375, fontSize: 14, fontWeight: 600 }}
      >
        Sign In
      </Button>
    </Box>
  );
};

export default RegisterPage;